<template>
  <div class="page">
    <!-- 标题 -->
    <header class="hd">
      <h1 class="title">购车险享优惠</h1>
      <p class="subtitle">优选车险方案，投保即享多重优惠券</p>
      <div class="hd-notes">
        <p>1. 本平台由喜喜车（甘肃）物业管理有限责任公司负责开发运营；保险相关业务资质由兰州营业部提供。</p>
        <p>2. 保险产品为人保正规产品，一切权利义务以保险单及保险条款为准。</p>
      </div>
      <div class="hd-foot">
        <span class="hd-beian" @click="showBeian = true">备案号：陇ICP备XXXXXXXX号</span>
        <span class="hd-copyright">© 2026 喜喜车（甘肃）物业管理有限责任公司 版权所有</span>
      </div>
    </header>

    <!-- tabs -->
    <nav class="tabs">
      <div class="tab" :class="{ active: tab === 'desc' }" @click="tab = 'desc'">优惠说明</div>
      <div class="tab" :class="{ active: tab === 'form' }" @click="tab = 'form'">表单填写</div>
    </nav>

    <section class="body">
      <!-- 优惠说明 -->
      <div v-if="tab === 'desc'">
        <div v-if="loading" class="tip">加载中…</div>
        <div v-else-if="error" class="tip err">{{ error }}</div>
        <template v-else>
          <div class="group" v-for="g in grouped" :key="g.name">
            <div class="group-title">{{ g.name }}</div>
            <div class="cfg" v-for="c in g.items" :key="c.id">
              <div class="cfg-desc">{{ c.insuranceDesc || '—' }}</div>
              <div class="coupons">
                <span class="cp" v-for="(cp, idx) in parseCoupons(c.couponArrayStr)" :key="idx">
                  {{ cp.name }} ×{{ cp.quantity }}
                </span>
                <span v-if="!parseCoupons(c.couponArrayStr).length" class="cp empty">无优惠券</span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- 表单填写 -->
      <div v-else>
        <!-- 险种单选：按依赖关系分组，依赖项在主险不选时置灰禁用 -->
        <div class="ins-card" v-for="grp in insuranceGroups" :key="grp.group">
          <div class="ins-card-title">{{ grp.label }}</div>
          <div class="ins-row" v-for="row in grp.rows" :key="row.key">
            <div class="ins-label">{{ row.label }}</div>
            <div class="ins-opts">
              <div
                v-for="opt in row.options"
                :key="opt"
                class="ins-opt"
                :class="{ active: insurance[row.key] === opt, disabled: isRowDisabled(row.key) }"
                @click="onSelect(row.key, opt)"
              >{{ optionLabel(opt) }}</div>
            </div>
          </div>
        </div>

        <!-- 车险详情预览（自动生成，随选择实时更新） -->
        <div class="preview-box">
          <div class="preview-title">车险详情预览</div>
          <pre class="preview">{{ composeInsuranceTxt() }}</pre>
        </div>

        <!-- 联系信息 -->
        <div class="form">
          <label class="fld">
            <span class="lbl">手机号 <i>*</i></span>
            <input v-model.trim="mobile" type="tel" maxlength="11" placeholder="请输入手机号" />
          </label>
          <label class="fld">
            <span class="lbl">备注</span>
            <input v-model="remark" type="text" placeholder="选填" />
          </label>
        </div>

        <!-- 证件上传 -->
        <div class="form uploads">
          <div class="upload-item" v-for="u in uploadMeta" :key="u.key">
            <span class="lbl">{{ u.label }} <i v-if="u.required">*</i></span>
            <div class="upload-row">
              <label class="upload-btn">
                <input type="file" accept="image/*" @change="onUpload($event, u.key)" :disabled="uploading" />
                <span v-if="!imgUrl(u.key)" class="upload-ph">点击上传</span>
                <img v-else :src="imgUrl(u.key)" class="thumb" />
              </label>
              <span class="upload-state" v-if="uploading && pendingKey === u.key">上传中…</span>
              <span class="upload-del" v-else-if="imgUrl(u.key)" @click="clearImg(u.key)">移除</span>
            </div>
          </div>
        </div>

        <button class="submit" :disabled="submitting" @click="submit">
          {{ submitting ? '提交中…' : '提交投保' }}
        </button>
      </div>
    </section>

    <!-- 备案图片弹窗 -->
    <div class="beian-mask" v-if="showBeian" @click="showBeian = false">
      <img class="beian-img" :src="beianImg" @click.stop />
    </div>
  </div>
</template>

<script>
import { getRebatePage, createRecord, uploadFile } from '@/api/insurance'

export default {
  name: 'HelloWorld',
  data() {
    return {
      tab: 'desc',
      configs: [],
      // 备案图片弹窗
      showBeian: false,
      beianImg: 'https://lloss.nangua.website/20261001/1bKDyYOOKoW8d654bd7fa00a5bb4adc54686e90e6293.png',
      loading: false,
      error: '',
      submitting: false,
      // 表单字段
      mobile: '',
      remark: '',
      // 证件图片
      xingshiImg: '',
      shenfenFront: '',
      shefenBack: '',
      uploading: false,
      pendingKey: '',
      // 上传项定义
      uploadMeta: [
        { key: 'xingshiImg', label: '行驶证', required: true },
        { key: 'shenfenFront', label: '身份证正面', required: true },
        { key: 'shefenBack', label: '身份证背面', required: true }
      ],
      // 各险种当前选择：每行单选，最多一个选中值
      insurance: {
        compulsory: 'selected', // 交强险（锁定必买）
        vehicleTax: 'selected', // 车船税（跟随交强险）
        damage: 'none', // 车损险
        thirdParty: 'none', // 三者险
        driverSeat: 'none', // 司机座位险
        passengerSeat: 'none', // 乘客座位险
        thirdPartyMedical: 'none', // 三者医保外用药
        driverMedical: 'none', // 司机医保外用药
        passengerMedical: 'none', // 乘客医保外用药
        driverAccident: 'none' // 驾意险
      },
      // 单选组定义（按 group 分组展示）
      insuranceRows: [
        { key: 'compulsory', label: '交强险', options: ['selected'], group: 'compulsory' },
        { key: 'vehicleTax', label: '车船税', options: ['selected'], group: 'compulsory' },
        { key: 'thirdParty', label: '三者险', options: ['none', '200', '300', '500'], group: 'thirdParty' },
        { key: 'damage', label: '车损险', options: ['none', 'selected'], group: 'thirdParty' },
        { key: 'thirdPartyMedical', label: '三者医保外用药', options: ['none', '20', '30', 'shared'], group: 'thirdParty' },
        { key: 'driverSeat', label: '司机座位险', options: ['none', '2', '5', '10'], group: 'seat' },
        { key: 'driverMedical', label: '司机医保外用药', options: ['none', '2', '5', 'shared'], group: 'seat' },
        { key: 'passengerSeat', label: '乘客座位险', options: ['none', '2', '5', '10'], group: 'seat' },
        { key: 'passengerMedical', label: '乘客医保外用药', options: ['none', '2', '5', 'shared'], group: 'seat' },
        { key: 'driverAccident', label: '驾意险', options: ['none', 'selected'], group: 'accident' }
      ]
    }
  },
  computed: {
    // 优惠说明：按 name 相同分块展示
    grouped() {
      const map = {}
      this.configs.forEach((c) => {
        const key = c.name || '未命名方案'
        ;(map[key] = map[key] || []).push(c)
      })
      return Object.keys(map).map((name) => ({ name, items: map[name] }))
    },
    // 表单：按依赖关系分组（依赖项保持可见，仅置灰禁用）
    insuranceGroups() {
      const order = ['compulsory', 'thirdParty', 'seat', 'accident']
      const labelMap = {
        compulsory: '法定强制险',
        thirdParty: '三者险组合',
        seat: '座位险组合',
        accident: '驾意险'
      }
      const map = {}
      this.insuranceRows.forEach((r) => {
        (map[r.group] = map[r.group] || []).push(r)
      })
      return order
        .map((g) => (map[g] ? { group: g, label: labelMap[g], rows: map[g] } : null))
        .filter(Boolean)
    }
  },
  created() {
    this.loadConfigs()
  },
  methods: {
    async loadConfigs() {
      this.loading = true
      this.error = ''
      try {
        const data = await getRebatePage({ pageNo: 1, pageSize: 100 })
        this.configs = (data && data.list) || []
        if (!this.configs.length) {
          this.error = '暂无可展示的保险方案'
        }
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    parseCoupons(str) {
      if (!str) return []
      try {
        const arr = JSON.parse(str)
        return Array.isArray(arr) ? arr : []
      } catch (e) {
        return []
      }
    },
    // 选项值 -> 展示文案
    optionLabel(opt) {
      const map = {
        none: '不选',
        selected: '选中',
        '200': '200万', '300': '300万', '500': '500万',
        '2': '2万', '5': '5万', '10': '10万',
        '20': '20万', '30': '30万',
        shared: '共享'
      }
      return map[opt] || opt
    },
    // 该行是否禁用（置灰不可选）
    isRowDisabled(key) {
      const i = this.insurance
      if (key === 'compulsory') return true // 法定强制，锁定选中
      if (key === 'vehicleTax') return i.compulsory === 'selected' // 交强险选中则车船税联动锁定
      if (key === 'damage') return i.thirdParty === 'none' // 车损依赖三者
      if (key === 'thirdPartyMedical') return i.thirdParty === 'none' // 三者医保外依赖三者
      if (key === 'driverMedical') return i.driverSeat === 'none' // 司机医保外依赖司机座位
      if (key === 'passengerMedical') return i.passengerSeat === 'none' // 乘客医保外依赖乘客座位
      return false
    },
    // 选择某险种选项：同行互斥（每行只一个选中值），并刷新联动
    onSelect(key, opt) {
      if (this.isRowDisabled(key)) return
      this.insurance[key] = opt
      this.enforceLinks()
    },
    // 联动刷新：被依赖项变为「不选」时，其下属强制回到「不选」；交强险选中则车船税强制选中
    enforceLinks() {
      const i = this.insurance
      if (i.thirdParty === 'none') {
        i.damage = 'none'
        i.thirdPartyMedical = 'none'
      }
      if (i.driverSeat === 'none') {
        i.driverMedical = 'none'
      }
      if (i.passengerSeat === 'none') {
        i.passengerMedical = 'none'
      }
      if (i.compulsory === 'selected') {
        i.vehicleTax = 'selected' // 交强险选中，车船税强制选中并锁定
      }
    },
    // 根据当前选择生成车险详情文本（提交内容）
    composeInsuranceTxt() {
      return this.insuranceRows
        .map((r) => `${r.label}：${this.optionLabel(this.insurance[r.key])}`)
        .join('\n')
    },
    imgUrl(key) {
      return this[key]
    },
    clearImg(key) {
      this[key] = ''
    },
    async onUpload(e, key) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.uploading = true
      this.pendingKey = key
      try {
        const url = await uploadFile(file, 'insurance')
        this[key] = url
      } catch (err) {
        alert(err.message || '上传失败')
      } finally {
        this.uploading = false
        this.pendingKey = ''
        e.target.value = ''
      }
    },
    async submit() {
      if (!this.mobile) {
        alert('请输入手机号')
        return
      }
      if (!/^1[3-9]\d{9}$/.test(this.mobile)) {
        alert('手机号格式不正确')
        return
      }
      if (!this.xingshiImg || !this.shenfenFront || !this.shefenBack) {
        alert('请上传行驶证、身份证正反面')
        return
      }
      this.submitting = true
      try {
        const body = {
          mobile: this.mobile,
          // 车险详情：必填，由险种选择自动生成
          insuranceTxt: this.composeInsuranceTxt(),
          // 返佣金额由后台 admin 编辑补充，H5 提交时默认 0
          rebateAmount: 0,
          remark: this.remark,
          // 证件图片
          xingshiImg: this.xingshiImg,
          shenfenFront: this.shenfenFront,
          shefenBack: this.shefenBack
        }
        await createRecord(body)
        alert('提交成功，我们的顾问会尽快与您联系！')
        this.resetForm()
      } catch (e) {
        alert(e.message || '提交失败')
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      this.mobile = ''
      this.remark = ''
      this.xingshiImg = ''
      this.shenfenFront = ''
      this.shefenBack = ''
      this.insurance = {
        compulsory: 'selected',
        vehicleTax: 'selected',
        damage: 'none',
        thirdParty: 'none',
        driverSeat: 'none',
        passengerSeat: 'none',
        thirdPartyMedical: 'none',
        driverMedical: 'none',
        passengerMedical: 'none',
        driverAccident: 'none'
      }
    }
  }
}
</script>

<style scoped>
.page {
  max-width: 640px;
  margin: 0 auto;
  background: #f5f6f8;
  min-height: 100vh;
}

/* 标题区 */
.hd {
  background: linear-gradient(135deg, #ff5b4a, #e4393c);
  color: #fff;
  padding: 36px 20px 28px;
  text-align: center;
}
.title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 2px;
}
.subtitle {
  margin: 10px 0 0;
  font-size: 14px;
  opacity: 0.9;
}

/* tabs */
.tabs {
  display: flex;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
.tab {
  flex: 1;
  text-align: center;
  padding: 14px 0;
  font-size: 15px;
  color: #646a73;
  cursor: pointer;
  position: relative;
}
.tab.active {
  color: #e4393c;
  font-weight: 600;
}
.tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  border-radius: 3px;
  background: #e4393c;
}

.body {
  padding: 14px;
}

.tip {
  text-align: center;
  color: #8a9099;
  padding: 40px 0;
  font-size: 14px;
}
.tip.err {
  color: #e4393c;
}

/* 优惠说明分组卡片 */
.group {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.group-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2329;
  padding: 14px 16px 8px;
  background: #fafbfc;
  border-bottom: 1px solid #eee;
}
.cfg {
  padding: 14px 16px;
  border-bottom: 1px solid #f2f3f5;
}
.cfg:last-child {
  border-bottom: none;
}
.cfg-desc {
  font-size: 14px;
  color: #1f2329;
  line-height: 1.5;
  white-space: pre-wrap;
}
.coupons {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.cp {
  font-size: 12px;
  color: #e4393c;
  background: #fff0ee;
  border: 1px solid #ffd9d3;
  border-radius: 4px;
  padding: 2px 8px;
}
.cp.empty {
  color: #8a9099;
  background: #f2f3f5;
  border-color: #e5e6eb;
}

/* 标题区小字说明 */
.hd-notes {
  margin: 14px auto 0;
  max-width: 520px;
  text-align: left;
  font-size: 11px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);
}
.hd-notes p {
  margin: 0 0 4px;
}
.hd-notes p:last-child {
  margin-bottom: 0;
}

/* 险种卡片 */
.ins-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.ins-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2329;
  padding: 14px 16px 8px;
  background: #fafbfc;
  border-bottom: 1px solid #eee;
}
.ins-row {
  display: flex;
  align-items: flex-start;
  padding: 14px 16px;
  border-bottom: 1px solid #f2f3f5;
}
.ins-row:last-child {
  border-bottom: none;
}
.ins-label {
  width: 110px;
  flex-shrink: 0;
  font-size: 14px;
  color: #1f2329;
  padding-top: 8px;
}
.ins-opts {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}
.ins-opt {
  font-size: 13px;
  color: #1f2329;
  border: 1px solid #dcdfe6;
  border-radius: 16px;
  padding: 7px 14px;
  cursor: pointer;
  background: #fff;
  transition: all 0.15s;
}
.ins-opt.active {
  color: #fff;
  background: #e4393c;
  border-color: #e4393c;
  font-weight: 600;
}
.ins-opt.disabled {
  color: #b7bcc4;
  background: #f5f6f8;
  border-color: #ebedf0;
  cursor: not-allowed;
}

/* 车险详情预览 */
.preview-box {
  background: #fff;
  border-radius: 12px;
  padding: 12px 16px 14px;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.preview-title {
  font-size: 13px;
  font-weight: 600;
  color: #646a73;
  margin-bottom: 8px;
}
.preview {
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  color: #1f2329;
  white-space: pre-wrap;
  word-break: break-all;
}

/* 表单 */
.form {
  background: #fff;
  border-radius: 12px;
  padding: 6px 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.fld {
  display: block;
  padding: 12px 0;
  border-bottom: 1px solid #f2f3f5;
}
.fld:last-child {
  border-bottom: none;
}
.lbl {
  display: block;
  font-size: 14px;
  color: #1f2329;
  margin-bottom: 8px;
  font-weight: 500;
}
.lbl i {
  color: #e4393c;
  font-style: normal;
}
.fld input {
  width: 100%;
  border: none;
  outline: none;
  font-size: 14px;
  color: #1f2329;
  background: transparent;
}
.fld input::placeholder {
  color: #b7bcc4;
}

/* 证件上传 */
.uploads {
  padding-bottom: 14px;
}
.upload-item {
  padding: 12px 0;
  border-bottom: 1px solid #f2f3f5;
}
.upload-item:last-child {
  border-bottom: none;
}
.upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.upload-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  overflow: hidden;
  background: #fafbfc;
  color: #8a9099;
  font-size: 13px;
  cursor: pointer;
}
.upload-btn input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.upload-btn .thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.upload-state {
  font-size: 13px;
  color: #e4393c;
}
.upload-del {
  font-size: 13px;
  color: #e4393c;
  cursor: pointer;
}

.submit {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 24px;
  background: linear-gradient(135deg, #ff5b4a, #e4393c);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}
.submit:disabled {
  opacity: 0.6;
}

/* 标题区底部备案与版权 */
.hd-foot {
  margin-top: 12px;
  font-size: 11px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.8);
}
.hd-beian {
  display: inline-block;
  color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  text-decoration: underline;
  margin-right: 12px;
}
.hd-copyright {
  color: rgba(255, 255, 255, 0.7);
}

/* 备案图片弹窗 */
.beian-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 24px;
}
.beian-img {
  max-width: 90%;
  max-height: 82%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
}
</style>
